/* eslint-disable max-len */
import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {UsageUrl} from '../../page';
import {ExampleCode} from '../ExampleCode';
import {ResponseTable} from '../tables/ResponseTable';
import {ParamTable} from '../tables/ParamTable';
import {ENDPOINTS_CODE} from '../../constants/code/_code';
import {Link} from '../../../common/components';

export const EndpointsSection = () => {
    return (
        <div id="endpoints">
            <h1>Endpoints</h1>
            <section id="endpoints-search">
                <h2>Search</h2>
                <section className="narrow">
                    <h4>Base Url:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/search?<parameters>`}/>

                    <p>Get a json object containing a list of the most relevant GIFs for a given search term(s), category(ies), emoji(s), or any combination thereof.</p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>    Provide the user’s search as typed, including punctuation and special characters.</li>
                        <li>    Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit search results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.</li>
                        <li>    When a user decides which GIF to share, you should also include a corresponding call to <Link to='#endpoints-registershare'>Register Share</Link>. This optional call helps Tenor’s Search Engine AI tune results.</li>
                        <li>    Use the limit and pos parameters to control the amount and flow of GIFs returned. For example, set limit = 10 for the user’s initial results of a given search and load previews of those GIFs for the user to browse. If the user requests more results, collect the next 10 results by making the same API call but with pos = the “next” field from the initial response. This pattern can be used to create a smooth lazy loading experience. Doing so will help keep bandwidth usage down and provide a quicker response time for the user — as less GIF previews will need to be loaded in parallel on the client’s side.</li>
                        <li>    Use the <Link to='#endpoints-search-params'>ContentFilter parameter</Link> to maintain your internal content safety ratings for GIFs returned. The default value is off</li>
                        <li>    Use the media_filter parameter to reduce the number of GIF formats returned. This can reduce the response object size by 25-75%.</li>
                        <li>    Use the ar_range parameter to filter the GIF response list to only include aspect ratios that fall within the selected range.</li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-search-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'q', status: 'REQUIRED', type: 'string', description: 'a search string'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'contentfilter', status: 'STRONGLY RECOMMENDED', type: 'enum', default: 'off', description: '(values: off | low | medium | high) specify the content safety filter level'},
                        {name: 'media_filter', status: 'STRONGLY RECOMMENDED', type: 'string', description: ['(values: basic | minimal) Reduce the Number of GIF formats returned in the GIF_OBJECT list.', 'minimal - tinygif, gif, and mp4.', 'basic - nanomp4, tinygif, tinymp4, gif, mp4, and nanogif']},
                        {name: 'ar_range', status: 'STRONGLY RECOMMENDED', type: 'string', default: 'all', description: ['(values: all | wide | standard ) Filter the response GIF_OBJECT list to only include GIFs with aspect ratios that fit with in the selected range.', 'all - no constraints', 'wide - 0.42 <= aspect ratio <= 2.36', 'standard - .56 <= aspect ratio <= 1.78']},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'pos', status: 'OPTIONAL', type: 'string', description: 'get results starting at position "value". Use a non-zero "next" value returned by API results to get the next set of results. pos is not an index and may be an integer, float, or string'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'next', type: 'string', description: 'a position identifier to use with the next API query to retrieve the next set of results, or 0 if there are no further results.'},
                            {key: 'results', type: <Link to='#responseobjects-gif'>GIF_OBJECT[]</Link>, description: 'an array of GIF_OBJECTS containing the most relevant GIFs for the requested search term - Sorted by relevancy Rank'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['search']} />
                </section>
            </section>


            <section id="endpoints-trendinggifs">
                <h2>Trending GIFs</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/trending?<parameters>`}/>
                    <p>Get a json object containing a list of the current global trending GIFs. The trending stream is updated regularly throughout the day.</p>
                </section>

                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit trending results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.
                        </li>
                        <li>
                When a user decides which GIF to share, you should also include a corresponding call to <Link to='#endpoints-registershare'>Register Share</Link>. This optional call helps Tenor’s Search Engine AI tune results.
                        </li>
                        <li>
                Use the Limit and Pos parameters to control the amount and flow of GIFs returned and ultimately loaded. For example, set limit = 10 for the user’s initial results of a trending request and load previews of those GIFs for the user to browse. If the user requests more results, collect the next 10 results by making the same API call but with Pos = the “next” field from the initial response. This pattern can be used to create a smooth lazy loading experience. Doing so will help keep bandwidth usage down and provide a quicker response time for the user as less GIF previews will need to be loaded in parallel on client side.
                        </li>
                        <li>
                Use the <Link to='#contentfilter'>ContentFilter Parameter</Link> to specify the appropriate GIF content safety rating for your service or application.
                        </li>
                        <li>
                Use the media_filter parameter to reduce the number of GIF formats returned. This can reduce the response object size by 25-75%.
                        </li>
                        <li>
                Use the ar_range parameter to filter the GIF response list to only include aspect ratios that fall within the selected range.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-trendinggifs-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'media_filter', status: 'STRONGLY RECOMMENDED', type: 'string', description: ['(values: basic | minimal) Reduce the Number of GIF formats returned in the GIF_OBJECT list.', 'minimal - tinygif, gif, and mp4.', 'basic - nanomp4, tinygif, tinymp4, gif, mp4, and nanogif']},
                        {name: 'ar_range', status: 'STRONGLY RECOMMENDED', type: 'string', default: 'all', description: ['(values: all | wide | standard ) Filter the response GIF_OBJECT list to only include GIFs with aspect ratios that fit with in the selected range.', 'all - no constraints', 'wide - 0.42 <= aspect ratio <= 2.36', 'standard - .56 <= aspect ratio <= 1.78']},
                        {name: 'contentfilter', status: 'STRONGLY RECOMMENDED', type: 'enum', default: 'off', description: '(values: off | low | medium | high) specify the content safety filter level'},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'pos', status: 'OPTIONAL', type: 'string', description: 'get results starting at position "value". Use a non-zero "next" value returned by API results to get the next set of results. pos is not an index and may be an integer, float, or string'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'next', type: 'string', description: 'a position identifier to use with the next API query to retrieve the next set of results, or 0 if there are no further results.'},
                            {key: 'results', type: <Link to='#responseobjects-gif'>GIF_OBJECT[]</Link>, description: 'an array of Trending GIF_OBJECTS sorted by Trending Rank'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['trendinggifs']} />
                </section>
            </section>

            <section id="endpoints-categories">
                <h2>Categories</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/categories?<parameters>`}/>
                    <p>
                Get a json object containing a list of GIF categories associated with the provided type. Each category will include a corresponding search URL to be used if the user clicks on the category. The search URL will include the apikey, anonymous id, and locale that were used on the original call to the categories endpoint.
                    </p>
                    <h4>Supported types:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                            <b>featured (default)</b> - The current featured emotional / reaction based GIF categories including a preview GIF for each term.
                        </li>
                        <li>
                            <b>emoji</b> - The current featured emoji GIF categories
                        </li>
                        <li>
                            <b>trending</b> - The current trending search terms including a preview GIF for each term.
                        </li>
                    </ol>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the Locale parameter to convert category names to the user’s language. The default value is EN_US.
                        </li>
                        <li>
                    Use the <Link to='#contentfilter'>ContentFilter Parameter</Link> to specify the appropriate GIF content safety rating for your service or application. When used, the ContentFilter parameter will be passed through to all search urls found in the categories response object.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-categories-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'type', status: 'STRONGLY RECOMMENDED', type: 'string', default: 'featured', description: '(values: featured | emoji | trending ) determines the type of categories returned'},
                        {name: 'contentfilter', status: 'STRONGLY RECOMMENDED', type: 'enum', default: 'off', description: '(values: off | low | medium | high) specify the content safety filter level'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'tags', type: <Link to='#responseobjects-category'>CATEGORY_OBJECT[]</Link>, description: 'an array of CATEGORY_OBJECTS where the “name” field has been translated to the passed in locale language.'},
                        ]}/>
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['categories']} />
                </section>
            </section>

            <section id="endpoints-suggestions">
                <h2>Search Suggestions</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/search_suggestions?<parameters>`}/>
                    <p>
                Get a json object containing a list of alternative search terms given a search term.
                    </p>
                    <p>
                Search suggestions helps a user narrow their search or discover related search terms to find a more precise GIF. Results are returned in order of what is most likely to drive a share for a given term, based on historic user search and share behavior.
                    </p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit search results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.
                        </li>
                        <li>
                    Display the results in the order provided by the response object.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-suggestions-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'q', status: 'REQUIRED', type: 'string', description: 'a search string'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'results', type: 'String[]', description: 'An array of suggested search terms.'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['suggestions']} />
                </section>
            </section>

            <section id="endpoints-autocomplete">
                <h2>Autocomplete</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/autocomplete?q=<term>&key=<API KEY>`}/>
                    <p>
                Get a json object containing a list of completed search terms given a partial search term. The list is sorted by Tenor’s AI and the number of results will decrease as Tenor’s AI becomes more certain.
                    </p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit search results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.
                        </li>
                        <li>
                    Display the results in the order provided by the response object.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-autocomplete-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'q', status: 'REQUIRED', type: 'string', description: 'a search string'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'results', type: 'String[]', description: 'An array of suggested search terms.'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['autocomplete']} />
                </section>
            </section>

            <section id="endpoints-trendingterms">
                <h2>Trending Search Terms</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/trending_terms?<parameters>`}/>
                    <p>Get a json object containing a list of the current trending search terms. The list is updated Hourly by Tenor’s AI.</p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit search results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.
                        </li>
                        <li>
                    Display the results in the order provided by the response object.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-trendingterms-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'results', type: 'String[]', description: 'An array of suggested search terms. Sorted by Trending Rank.'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['trendingterms']} />
                </section>
            </section>


            <section id="endpoints-registershare">
                <h2>Register Share</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/registershare?<parameters>`}/>
                    <p>Register a user’s sharing of a GIF.</p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                            Provide the search term. This helps further tune Tenor’s Search Engine AI, helping users more easily find the perfect GIF.
                        </li>
                        <li>
                            Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. The default value is en_US.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-registershare-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'id', status: 'REQUIRED', type: 'string', description: `the “id” of a GIF_OBJECT`},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'q', status: 'STRONGLY RECOMMENDED', type: 'string', description: 'The search string that lead to this share'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'status', type: 'String', description: `set to “ok” if share registration was successful`},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['registershare']} />
                </section>
            </section>


            <section id="endpoints-gifs">
                <h2>GIFs</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/gifs?<parameters>`}/>
                    <p>Get the GIF(s) for the corresponding id(s)</p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the media_filter parameter to reduce the number of GIF formats returned. This can reduce the response object size by 25-75%.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-gifs-params" content={[
                        {name: 'ids', status: 'REQUIRED', type: 'string', description: 'a comma separated list of GIF IDs (max: 50)'},
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'media_filter', status: 'STRONGLY RECOMMENDED', type: 'string', description: ['(values: basic | minimal) Reduce the Number of GIF formats returned in the GIF_OBJECT list.', 'minimal - tinygif, gif, and mp4.', 'basic - nanomp4, tinygif, tinymp4, gif, mp4, and nanogif']},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'pos', status: 'OPTIONAL', type: 'string', description: 'get results starting at position "value". Use a non-zero "next" value returned by API results to get the next set of results. pos is not an index and may be an integer, float, or string'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'next', type: 'string', description: 'a position identifier to use with the next API query to retrieve the next set of results, or 0 if there are no further results.'},
                            {key: 'results', type: <Link to='#responseobjects-gif'>GIF_OBJECT[]</Link>, description: 'an array of GIF_OBJECTS corresponding to the passed in GIF id list'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['gifs']} />
                </section>
            </section>


            <section id="endpoints-random">
                <h2>Random GIFs</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/random?<parameters>`}/>
                    <p>
                Get a randomized list of GIFs for a given search term. This differs from the search endpoint which returns a rank ordered list of GIFs for a given search term.
                    </p>
                </section>
                <section className="narrow">
                    <h4>Best Practices:</h4>
                    <ol className="numbered-list indent-align">
                        <li>
                    Use the Locale parameter. As Tenor’s service evolves, locale will be used to better tune search results to your users’ specific languages, cultures, and social trends. Using Locale will give higher preference to the user’s given locale, though it will not limit search results to only the provided code and language typed, but gives higher preference to the given locale. The default value is en_US.
                        </li>
                        <li>
                    Use the <Link to='#contentfilter'>ContentFilter parameter</Link> to maintain your internal content safety ratings for GIFs returned. The default value is off
                        </li>
                        <li>
                    Use the media_filter parameter to reduce the number of GIF formats returned. This can reduce the response object size by 25-75%.
                        </li>
                        <li>
                    Use the ar_range parameter to filter the GIF response list to only include aspect ratios that fall within the selected range.
                        </li>
                    </ol>
                </section>

                <section>
                    <ParamTable id="endpoints-random-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                        {name: 'q', status: 'REQUIRED', type: 'string', description: 'a search string'},
                        {name: 'locale', status: 'STRONGLY RECOMMENDED', type: 'string (xx_YY)', default: 'en_US', description: 'specify default language to interpret search string; xx is ISO 639-1 language code, _YY (optional) is 2-letter ISO 3166-1 country code'},
                        {name: 'contentfilter', status: 'STRONGLY RECOMMENDED', type: 'enum', default: 'off', description: '(values: off | low | medium | high) specify the content safety filter level'},
                        {name: 'media_filter', status: 'STRONGLY RECOMMENDED', type: 'string', description: ['(values: basic | minimal) Reduce the Number of GIF formats returned in the GIF_OBJECT list.', 'minimal - tinygif, gif, and mp4.', 'basic - nanomp4, tinygif, tinymp4, gif, mp4, and nanogif']},
                        {name: 'ar_range', status: 'STRONGLY RECOMMENDED', type: 'string', default: 'all', description: ['(values: all | wide | standard ) Filter the response GIF_OBJECT list to only include GIFs with aspect ratios that fit with in the selected range.', 'all - no constraints', 'wide - 0.42 <= aspect ratio <= 2.36', 'standard - .56 <= aspect ratio <= 1.78']},
                        {name: 'limit', status: 'OPTIONAL', type: 'integer', default: '20', description: 'fetch up to a specified number of results (max: 50).'},
                        {name: 'pos', status: 'OPTIONAL', type: 'string', description: 'get results starting at position "value". Use a non-zero "next" value returned by API results to get the next set of results. pos is not an index and may be an integer, float, or string'},
                        {name: 'anon_id', status: 'OPTIONAL', type: 'string', description: 'specify the anonymous_id tied to the given user'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'next', type: 'string', description: 'a position identifier to use with the next API query to retrieve the next set of results, or 0 if there are no further results.'},
                            {key: 'results', type: <Link to='#responseobjects-gif'>GIF_OBJECT[]</Link>, description: 'an array of GIF_OBJECTS containing the most relevant GIFs for the requested search term - Sorted by relevancy Rank'},
                        ]}/>
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['random']} />
                </section>
            </section>


            <section id="endpoints-anonymousid">
                <h2>Anonymous ID</h2>
                <section className="narrow">
                    <h4>Base URL:</h4>
                    <UsageUrl url={`https://g.tenor.com/v1/anonid?<parameters>`}/>
                    <p>
                        Get an anonymous ID for a new user. Store the ID in the client’s cache for use on any additional API calls made by the user, either in this session or any future sessions. Note: using anonymous ID to personalize API responses requires custom development. Please see the <Link to='#custom'>Custom APIs</Link> section for more detail.
                    </p>
                </section>

                <section>
                    <ParamTable id="endpoints-anonymousid-params" content={[
                        {name: 'key', status: 'REQUIRED', type: 'string', description: 'client key for privileged API access'},
                    ]}/>
                </section>

                <section>
                    <ResponseTable
                        content={[
                            {key: 'anon_id', type: 'string', description: 'an anonymous id used to represent a user. This allows for tracking without the use of personally identifiable information'},
                        ]}
                    />
                </section>

                <section>
                    <ExampleCode code={ENDPOINTS_CODE['anonymousid']} />
                </section>
            </section>


            <section id="endpoints-domains">
                <h2>g.tenor.com Domain</h2>
                <p>
                    {`Tenor is excited to offer a new domain for our API developers,
                    g.tenor.com. While the domain api.tenor.com will continue to be
                    supported, g.tenor.com offers significant benefits. g.tenor.com
                    has an additional cache layer and is located at edge nodes across
                    the world, resulting in lower average API latencies across all
                    regions. As such, Tenor recommends using g.tenor.com and has
                    updated the documentation to reflect that.`}
                </p>
                <h4>
                    {`How to switch domains in an existing integration:`}
                </h4>
                <p>
                    {`Switching domains is a painless process, simply replace any
                    reference to "api.tenor.com" with "g.tenor.com" in your Tenor
                    API url requests. For example, a search call would be updated
                    as follows.`}
                </p>
                <p>
                    <b>from</b>
                    <br/>
                    <i style={{'margin-left': '12px'}}>{`"https://api.tenor.com/v1/search?q=excited&key=LIVDSRZULELA&limit=8"`}</i>
                    <br/>
                    <b>to</b>
                    <br/>
                    <i style={{'margin-left': '12px'}}>{`"https://g.tenor.com/v1/search?q=excited&key=LIVDSRZULELA&limit=8"`}</i>
                </p>
            </section>
        </div>
    );
};
